// src/layouts/MainLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from "../services/operations/authAPI";
// Import both API calls
import { getMyAssignedObservations, getObservations } from '../services/observationAPI';

import {
    Menu,
    X,
    LogOut,
    Bell, // Keep Bell icon if needed elsewhere, but notification dot will be custom
    CheckSquare,
    ListChecks,
    BarChart,
    Home,
    Gamepad,
    User,
    Users,
    Settings,
    AreaChart,
    Verified,
    Building,
    ClipboardCheckIcon,
} from 'lucide-react';

export const Dashboard = () => {
    const { user } = useSelector((state) => state.profile);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    // State to hold pending action count (will be used for both roles)
    const [pendingActionCount, setPendingActionCount] = useState(0);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    // console.log("p",pendingActionCount)

    // Fetch pending actions based on user role
    useEffect(() => {
        const fetchPendingActions = async () => {
            if (!user) {
                setPendingActionCount(0);
                return;
            }

            try {
                let actionsToCount = [];

                if (user.accountType === 'Admin') {
                    // For Admin: Fetch all observations and count pending ones
                    const observationsRes = await getObservations();
                     // Assuming getObservations returns { observations: [...] }
                    actionsToCount = (observationsRes.observations || [])
                         .filter(item => item && typeof item === 'object');

                } else {
                    // For User: Fetch only assigned observations and count pending ones
                    const actionsRes = await getMyAssignedObservations();
                    actionsToCount = (actionsRes.observations || [])
                        .filter(item => item && typeof item === 'object');
                }

                // Count actions with 'waiting', 'in-progress', or 'submitted' status
                const pending = actionsToCount.filter(action =>
                    action.status === 'waiting' ||
                    action.status === 'in-progress' ||
                    action.status === 'submitted'
                ).length;

                setPendingActionCount(pending);

            } catch (error) {
                console.error('Error fetching pending actions:', error);
                setPendingActionCount(0); // Set to 0 on error
            }
        };

        // Fetch initially and potentially refetch periodically or based on events
        fetchPendingActions();

        // Optional: Set up polling to refresh the count periodically (e.g., every 5 minutes)
        // const intervalId = setInterval(fetchPendingActions, 300000); // 300000 ms = 5 minutes

        // Cleanup interval on component unmount
        // return () => clearInterval(intervalId);

    }, [user]); // Rerun effect if user changes

    const handleLogout = async () => {
        try {
            dispatch(logout(navigate));
        } catch (error) {
            console.error("Logout failed:", error);
            // Optionally display an error message to the user
        }
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const commonNavItems = [
        
        { path: '/dashboard/my-profile', icon: <User size={20} />, label: 'Profile' },
        { path: '/dashboard/Settings', icon: <Settings size={20} />, label: 'Setting' },
    ];

    const superAdminNavItems =[
        { path: '/super-dashboard', icon: <Home size={20} />, label: 'Dashboard' },
        { path: '/plants', icon: <Building size={20} />, label: 'Plants' },
        // { path: '/my-actions', icon: <Verified size={20} />, label: 'My Action', pendingCount: pendingActionCount },
        ...commonNavItems
        // { path: '/my-actions', icon: <Verified size={20} />, label: 'My Action', pendingCount: pendingActionCount },
    ]
    const adminNavItems = [
        { path: '/admin-dashboard', icon: <Home size={20} />, label: 'Dashboard' },
        { path: '/admin/area', icon: <AreaChart size={20} />, label: 'Area' },
        { path: '/admin/add-users', icon: <Users size={20} />, label: 'Users' },
        // Added pendingActionCount to the 'My Action' item for easy access during mapping
        { path: '/admin/my-actions', icon: <Verified size={20} />, label: 'My Action', pendingCount: pendingActionCount },
        { path: '/bbs/coaching-library', icon: <ClipboardCheckIcon size={20}/>, label: 'Coaching Library' },
        { path: '/bbs/games', icon: <Gamepad size={20}/>, label: 'Games' },
        ...commonNavItems,
    ];

    const userNavItems = [
        { path: '/user-dashboard', icon: <Home size={20} />, label: 'Dashboard' },
        { path: '/employee/submit-observation', icon: <CheckSquare size={20} />, label: 'Observation' },
        { path: '/user/observation', icon: <ListChecks size={20} />, label: 'My Observation' },
        // Added pendingActionCount to the 'My Action' item for easy access during mapping
        { path: '/employee/my-actions', icon: <Verified size={20} />, label: 'My Action', pendingCount: pendingActionCount },
        { path: '/coaching/my-coaching', icon: <CheckSquare size={20} />, label: 'My Coaching' },
        { path: '/bbs/coaching-library', icon: <ClipboardCheckIcon size={20}/>, label: 'Coaching Library' },
        { path: '/bbs/games', icon: <Gamepad size={20}/>, label: 'Games' },
        ...commonNavItems,
    ];

    const navItems = 
  user?.accountType ==='SuperAdmin' 
    ? superAdminNavItems 
    : user?.accountType ==='Admin' 
      ? adminNavItems 
      : userNavItems;


    if (!user) {
        return <div>Loading or Not Authenticated...</div>;
    }
    
    return (
        <div className="flex bg-gray-50" style={{ height: 'calc(100vh - 65px)' }}>
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
                    <a href='/' className="flex items-center space-x-2">
                        <div className="bg-blue-100 p-1.5 rounded">
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                        </div>
                        <span className="text-lg font-semibold text-gray-900">BBS</span>
                    </a>
                    <button
                        className="lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X size={24} />
                    </button>
                </div>

                <nav className="mt-5 px-2 space-y-1">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors relative ${ // Added relative positioning for the dot
                                isActive(item.path)
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                        >
                            <span className="mr-3">{item.icon}</span>
                            {item.label}
                            {/* Conditional Notification Dot for My Action sidebar item */}
                            {(item.path === '/employee/my-actions' || item.path === '/admin/my-actions') && item.pendingCount > 0 && (
                                <span className="absolute top-2 right-2 block h-2 w-2 rounded-full bg-red-500"></span>
                            )}
                        </Link>
                    ))}
                </nav>

                <div className="absolute bottom-1 w-full p-4 border-gray-200">
                    <div className="flex items-center mb-4">
                        {/* User profile info can go here if needed */}
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full py-2.5 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none flex items-center justify-center space-x-2 border border-gray-300"
                    >
                        <LogOut size={16} />
                        <span>Sign Out</span>
                    </button>
                </div>
            </div>

            {/* Main content area */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* Header for all screen sizes */}
                <header className="bg-white shadow-sm"> {/* Removed lg:hidden */}
                    <div className="flex h-16 items-center justify-between px-4">
                        {/* Mobile menu button (only visible on small screens) */}
                        <button
                            className="text-gray-500 focus:outline-none lg:hidden" // Keep lg:hidden here
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu size={24} />
                        </button>

                        {/* Placeholder for potential center content on larger screens */}
                        <div className="flex-1 hidden lg:block">
                            {/* Add logo or title for larger screens if needed */}
                        </div>


                        {/* Notification icon (visible on all screen sizes) */}
                         <div className="flex items-center space-x-4">
                             <div className="relative p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none">
                             <Link to='/bbs/notification'> {/* Link to the notifications page */}
                                 <Bell size={20} />
                                 {/* Conditional Notification Dot with count */}
                                 {pendingActionCount > 0 && (
                                     <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 text-xs text-white text-center flex items-center justify-center"> {/* Added flex and justify/align for centering text */}
                                             {pendingActionCount}
                                     </span>
                                 )}
                             </Link>
                             </div>
                         </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-auto p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};
